import { AppDispatch } from "../../app/store";
import { fetchMarketingData } from "../../firebase/api/fetchMarketingData";
import {
  requestMagicLinkEmailAndAscendUser,
  requestMagicLinkEmail,
} from "../../firebase/api/requestLink";
import {
  createCampaign,
  updateCampaign,
} from "../../firebase/api/saveCampaign";
import { saveUserAnswers } from "../../firebase/api/saveUserAnswers";
import { saveUserFeedback } from "../../firebase/api/saveUserFeedback";
import { saveUserPlan } from "../../firebase/api/saveUserPlan";
import { useCombinedConstantsForCurrentCountry } from "../../firebase/db/constants";
import { extractLanguageCodeOrDefault } from "../../i18n/utils";
import { anonSessionSelectors } from "../store/slice/anonSessionSlice";
import {
  authedSessionActions,
  authedSessionSelectors,
} from "../store/slice/authedSessionSlice";
import { locationSelectors } from "../store/slice/locationSlice";
import { questionnaireSelectors } from "../store/slice/questionnaireSlice";
import {
  userAnswersActions,
  userAnswersSelectors,
} from "../store/slice/userAnswersSlice";
import { userPlanSelectors } from "../store/slice/userPlanSlice";
import { FirebaseError } from "@firebase/util";
import { NewPlan } from "@pslifestyle/common/src/schemas/api-payload";
import {
  UpdateCampaign,
  NewCampaign,
  EitherAnonOrLoggedInUser,
  LoggedInUser,
} from "@pslifestyle/common/src/types/api/payloadTypes";
import {
  WholeAction,
  CalculatedSkippedAction,
} from "@pslifestyle/common/src/types/planTypes";
import { t } from "i18next";
import { isEmpty } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useSWR from "swr";

type RequestState = {
  success: boolean;
  error: boolean;
  loading: boolean;
  errorMessage?: string;
};
const initialRequestState: RequestState = {
  success: false,
  error: false,
  loading: false,
};
const formatCreateActionPlanDto = (
  selectedActions: WholeAction[],
  alreadyDoThisActions: WholeAction[],
  skippedActions: CalculatedSkippedAction[],
): NewPlan => ({
  selectedActions: selectedActions.map((selectedAction) => ({
    id: selectedAction.id,
    state: selectedAction.state,
    ...(selectedAction.selectedGranularOption
      ? {
          selectedGranularOption:
            selectedAction.selectedGranularOption.globalName,
        }
      : {}),
  })),
  alreadyDoThisActions: alreadyDoThisActions.map((alreadyDoThisAction) => ({
    id: alreadyDoThisAction.id,
    state: "new",
  })),
  skippedActions: skippedActions.map((skippedAction) => ({
    id: skippedAction.id,
    reasons: skippedAction.reasons,
  })),
});

export const useEitherLoggedInOrAnonUser = (): EitherAnonOrLoggedInUser => {
  const authenticatedUser = useSelector(authedSessionSelectors.user);
  const anonUserId = useSelector(anonSessionSelectors.anonId);

  if (authenticatedUser) {
    return {
      email: authenticatedUser.email,
      sessionToken: authenticatedUser.sessionToken,
    };
  }

  if (!anonUserId) {
    throw new Error("User is missing anon ID");
  }
  return { anonId: anonUserId };
};

export const useLoggedInUser = (): LoggedInUser => {
  const authenticatedUser = useSelector(authedSessionSelectors.user);

  if (!authenticatedUser) {
    throw new Error("User is not authenticated to perform this action");
  }

  return {
    email: authenticatedUser.email,
    sessionToken: authenticatedUser.sessionToken,
  };
};

export function usePersistUserAnswers() {
  const dispatch = useDispatch<AppDispatch>();
  const [state, setState] = useState<RequestState>(initialRequestState);
  const reset = useCallback(() => setState(initialRequestState), []);
  const answers = useSelector(questionnaireSelectors.answers);
  const metadata = useSelector(questionnaireSelectors.metadata);
  const country = useSelector(locationSelectors.country);

  const sendAnswersToBackend = useCallback(async () => {
    try {
      if (!navigator.onLine) {
        throw new Error(t("error.noInternet", { ns: "common" }));
      }
      if (!country) {
        throw new Error("Country not set");
      }
      if (!metadata) {
        console.log("Questionnaire metadata not provided");
        throw new Error("Questionnaire metadata not provided");
      }

      setState({
        success: false,
        error: false,
        loading: true,
      });
      const calculatedAnswers = await saveUserAnswers({
        answers,
        metadata,
      });
      const {
        ordinaryAnswers: calculatedOrdinaryAnswers,
        answerSetId,
        categorizedFootprint,
      } = calculatedAnswers.data;
      dispatch(
        userAnswersActions.initializeUserAnswers({
          answerSetId,
          ordinaryAnswers: calculatedOrdinaryAnswers,
          categorizedFootprint,
        }),
      );
      setState({
        success: true,
        error: false,
        loading: false,
      });
    } catch (error: unknown) {
      setState({
        success: false,
        error: true,
        loading: false,
      });
    }
  }, [country, metadata, answers, dispatch]);

  return { sendAnswersToBackend, state, reset };
}

export function useAscendUser() {
  const initialState = {
    ...initialRequestState,
    userHasPlan: false,
  };
  const planExistsErrorCode = "functions/already-exists";

  const dispatch = useDispatch<AppDispatch>();
  const [state, setState] = useState<RequestState & { userHasPlan: boolean }>(
    initialState,
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reset = useCallback(() => setState(initialState), []);
  const answers = useSelector(questionnaireSelectors.answers);
  const metadata = useSelector(questionnaireSelectors.metadata);
  const language = useSelector(locationSelectors.language);
  const languageCode = extractLanguageCodeOrDefault(language);
  const anonId = useSelector(anonSessionSelectors.anonId);
  const country = useSelector(locationSelectors.country);
  const selectedActions = useSelector(userPlanSelectors.selectedActions);
  const alreadyDoThisActions = useSelector(
    userPlanSelectors.alreadyDoThisActions,
  );
  const skippedActions = useSelector(userPlanSelectors.skippedActions);

  const ascendUserToBackend = useCallback(
    async (email: string) => {
      if (!country) {
        console.log("cannot persist user plan, loading");
        return;
      }

      if (!anonId || !language || !metadata) {
        console.log("cannot persist user plan, missing anonId");
        return;
      }

      if (!Object.keys(selectedActions).length && !skippedActions.length) {
        return;
      }

      try {
        setState({
          loading: true,
          success: false,
          error: false,
          userHasPlan: false,
        });
        const actionPlanDto = formatCreateActionPlanDto(
          selectedActions,
          alreadyDoThisActions,
          skippedActions,
        );
        await requestMagicLinkEmailAndAscendUser(
          email,
          anonId,
          languageCode,
          country.code,
          { answers, metadata },
          actionPlanDto,
        );
        setState({
          loading: false,
          success: true,
          error: false,
          userHasPlan: false,
        });
        dispatch(authedSessionActions.setMagicLinkEmail(email));
      } catch (error) {
        if (error instanceof FirebaseError) {
          setState({
            loading: false,
            success: false,
            error: true,
            userHasPlan: error.code === planExistsErrorCode,
          });
          return;
        }
        setState({
          loading: false,
          success: false,
          error: true,
          userHasPlan: false,
        });
      }
    },
    [
      answers,
      anonId,
      country,
      dispatch,
      language,
      languageCode,
      metadata,
      selectedActions,
      alreadyDoThisActions,
      skippedActions,
    ],
  );

  return { ascendUserToBackend, state, reset };
}

// Combine constants with variables from user answers
export function useFullMathScope(): {
  loading: boolean;
  error: FirebaseError | undefined;
  fullMathScope: Record<string, number> | null;
} {
  const allVariables = useSelector(userAnswersSelectors.allVariables);
  const categorizedFootprint = useSelector(
    userAnswersSelectors.categorizedFootprint,
  );

  const {
    data: constantsResponseData,
    error: constantsError,
    isLoading: isLoadingConstants,
  } = useCombinedConstantsForCurrentCountry();

  const fullMathScope = useMemo(() => {
    if (constantsResponseData && !isEmpty(allVariables)) {
      const categoryTotalVariables = Object.entries(
        categorizedFootprint,
      ).reduce(
        (acc, [category, footprint]) => ({
          ...acc,
          [`ANSWER_CATEGORY_${category.toUpperCase()}_TOTAL`]: footprint || 0,
        }),
        {},
      );
      return {
        ...constantsResponseData[1],
        ...allVariables,
        ...categoryTotalVariables,
      };
    }
    return null;
  }, [constantsResponseData, allVariables, categorizedFootprint]);

  return {
    loading: isLoadingConstants,
    error: constantsError,
    fullMathScope,
  };
}

export function usePersistCampaign() {
  const [state, setState] = useState<RequestState>(initialRequestState);
  const reset = useCallback(() => setState(initialRequestState), []);
  const { t } = useTranslation("management");
  const sendCampaignToBackend = useCallback(
    async (campaign: NewCampaign | UpdateCampaign): Promise<void> => {
      try {
        setState({
          success: false,
          error: false,
          loading: true,
        });

        if ("id" in campaign) {
          await updateCampaign(campaign);
        } else {
          await createCampaign(campaign);
        }

        setState({
          success: true,
          error: false,
          loading: false,
        });
      } catch (error) {
        const errorState: RequestState = {
          success: false,
          error: true,
          loading: false,
        };
        // @ts-expect-error safely check if there is a code property
        if (error?.code === "functions/already-exists") {
          errorState.errorMessage = `${t("campaignManager.customCreateIdFormError")}
            "${(campaign as NewCampaign).customId}"`;
        }
        setState(errorState);
      }
    },
    [t],
  );
  return { sendCampaignToBackend, state, reset };
}

export function usePersistFeedback() {
  const [state, setState] = useState<RequestState>(initialRequestState);
  const reset = useCallback(() => setState(initialRequestState), []);
  const answerSetId = useSelector(userAnswersSelectors.answerSetId);
  const country = useSelector(locationSelectors.country);

  const sendFeedbackToBackend = useCallback(
    async (selectedOptions: string[]): Promise<void> => {
      try {
        if (!country) {
          throw new Error("Country not set");
        }

        if (!answerSetId) {
          throw new Error("Answer set is not created");
        }

        setState({
          success: false,
          error: false,
          loading: true,
        });
        await saveUserFeedback(answerSetId, {
          selectedOptions,
        });
        setState({
          success: true,
          error: false,
          loading: false,
        });
      } catch (error: unknown) {
        setState({
          success: false,
          error: true,
          loading: false,
        });
      }
    },
    [answerSetId, country],
  );
  return { sendFeedbackToBackend, state, reset };
}

export function usePersistUserPlan() {
  const [state, setState] = useState<RequestState>(initialRequestState);
  const reset = useCallback(() => setState(initialRequestState), []);
  const answerSetId = useSelector(userAnswersSelectors.answerSetId);
  const country = useSelector(locationSelectors.country);
  const selectedActions = useSelector(userPlanSelectors.selectedActions);
  const alreadyDoThisActions = useSelector(
    userPlanSelectors.alreadyDoThisActions,
  );
  const skippedActions = useSelector(userPlanSelectors.skippedActions);

  const sendPlanToBackend = useCallback(async () => {
    try {
      if (!country) {
        console.log("cannot persist user plan, loading");
        return;
      }

      if (!answerSetId) {
        throw new Error("Answer set is not created");
      }

      setState({
        success: false,
        error: false,
        loading: true,
      });
      const actionPlanDto = formatCreateActionPlanDto(
        selectedActions,
        alreadyDoThisActions,
        skippedActions,
      );
      await saveUserPlan(answerSetId, actionPlanDto);
      setState({
        success: true,
        error: false,
        loading: false,
      });
    } catch (error: unknown) {
      setState({
        success: false,
        error: true,
        loading: false,
      });
    }
  }, [
    country,
    answerSetId,
    selectedActions,
    alreadyDoThisActions,
    skippedActions,
  ]);

  return { sendPlanToBackend, state, reset };
}

export function useRequestMagicLink() {
  const dispatch = useDispatch<AppDispatch>();
  const [state, setState] = useState<RequestState>(initialRequestState);
  const reset = useCallback(() => setState(initialRequestState), []);
  const language = useSelector(locationSelectors.language);
  const languageCode = extractLanguageCodeOrDefault(language);
  const country = useSelector(locationSelectors.country);

  const anonId = useSelector(anonSessionSelectors.anonId);

  const requestMagicLink = useCallback(
    async (email: string) => {
      setState({ loading: true, success: false, error: true });
      if (!anonId) {
        console.log("Missing Anon ID login");
        setState({
          loading: false,
          success: false,
          error: true,
          errorMessage: "Error requesting magic link",
        });
        return;
      }

      try {
        await requestMagicLinkEmail(
          email,
          anonId,
          languageCode,
          country?.code ?? "",
        );
        setState({
          loading: false,
          success: true,
          error: false,
        });
        dispatch(authedSessionActions.setMagicLinkEmail(email));
      } catch (err) {
        console.log(err);
        setState({
          loading: false,
          success: false,
          error: true,
          errorMessage: "Error requesting magic link",
        });
      }
    },
    [anonId, dispatch, languageCode, country],
  );

  return { state, reset, requestMagicLink };
}

export function useMarketingData() {
  return useSWR(["marketingData"], async () => {
    const data = await fetchMarketingData();
    return data;
  });
}
